import { cn } from "cn";
import { Search, X } from "lucide-react";
import type * as React from "react";

// v3.2 fields (style.css inputs; web V3_INPUT and the review card's edit
// field): a sunken surface with a hairline, 10px radius, the brand ring on
// focus. 16px text on phones so iOS does not zoom, 15px from 1024px.
export const fieldClassName =
  "w-full min-w-0 rounded-[10px] border-0 bg-v3-surface-2 px-3 font-v3 text-[16px] text-v3-text shadow-v3-hairline outline-hidden transition-colors placeholder:text-v3-text-3 focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-2 aria-invalid:ring-[color-mix(in_srgb,var(--v3-rec)_45%,transparent)] lg:text-[15px]";

export function Input({
  className,
  type,
  ...props
}: React.ComponentPropsWithRef<"input">): React.JSX.Element {
  return (
    <input
      className={cn(fieldClassName, "h-11 lg:h-10", className)}
      data-slot="input"
      type={type ?? "text"}
      {...props}
    />
  );
}

export function Textarea({
  className,
  ...props
}: React.ComponentPropsWithRef<"textarea">): React.JSX.Element {
  return (
    <textarea
      className={cn(
        fieldClassName,
        "block min-h-24 resize-y py-2.5 leading-[22px]",
        className,
      )}
      data-slot="textarea"
      {...props}
    />
  );
}

// The search pill of the Visits and Templates lists: a magnifier, the field
// and a clear button once there is text.
export function SearchField({
  label,
  clearLabel,
  value,
  onValueChange,
  className,
  ...props
}: Omit<React.ComponentPropsWithRef<"input">, "value" | "onChange"> & {
  label: string;
  clearLabel: string;
  value: string;
  onValueChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className={cn("relative", className)}>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-v3-text-3"
      />
      <input
        aria-label={label}
        className="h-11 w-full rounded-full border-0 bg-v3-surface-2 pl-10 pr-11 font-v3 text-base text-v3-text shadow-v3-hairline placeholder:text-v3-text-3 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:h-9 lg:text-sm [&::-webkit-search-cancel-button]:hidden"
        placeholder={label}
        type="search"
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value);
        }}
        {...props}
      />
      {value && (
        <button
          aria-label={clearLabel}
          className="absolute right-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full text-v3-text-3 hover:text-v3-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:size-9"
          type="button"
          onClick={() => {
            onValueChange("");
          }}
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}
